import { ETLCourse } from './types';

export const CAPSTONE_PROJECT_COURSE: ETLCourse = {
  id: 'etl-capstone-projects',
  title: 'ETL Capstone Projects & Portfolio Development',
  description: 'Build real-world ETL projects for your portfolio. Work on industry-realistic scenarios and deploy production-ready solutions.',
  level: 'advanced',
  totalDuration: '20-25 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Portfolio',
  marketDemand: 'high',
  salaryRange: '$90,000 - $180,000',
  prerequisites: [
    'Completion of at least 3 ETL specialization courses',
    'Strong programming skills in Python/SQL',
    'Understanding of cloud platforms',
    'Basic project management knowledge'
  ],
  learningOutcomes: [
    'Build 3-5 production-ready ETL projects',
    'Create comprehensive technical documentation',
    'Develop professional portfolio presentations',
    'Master end-to-end project delivery',
    'Implement CI/CD for ETL deployments',
    'Prepare for senior-level technical interviews'
  ],
  modules: [
    {
      id: 'capstone-1',
      title: 'E-commerce Analytics Pipeline Project',
      description: 'Build a complete e-commerce analytics pipeline with real-time and batch processing',
      duration: '8 hours',
      difficulty: 'advanced',
      order: 1,
      objectives: [
        'Design multi-source data integration architecture',
        'Implement real-time customer behavior tracking',
        'Build dimensional data warehouse models',
        'Create executive dashboard and reporting'
      ],
      content: 'Build a comprehensive e-commerce analytics pipeline that processes customer data, product catalogs, order transactions, and web events. Integrate multiple data sources and create real-time dashboards for business insights.'
    },
    {
      id: 'capstone-2',
      title: 'Financial Risk Management ETL System',
      description: 'Develop a regulatory-compliant financial risk management system',
      duration: '7 hours',
      difficulty: 'advanced',
      order: 2,
      objectives: [
        'Implement regulatory compliance frameworks',
        'Build risk calculation and aggregation pipelines',
        'Design audit trails and data lineage',
        'Create automated regulatory reporting'
      ],
      content: 'Develop a financial risk management system that processes trading data, calculates risk metrics, and generates regulatory reports. Focus on compliance, audit trails, and data quality assurance.'
    },
    {
      id: 'capstone-3',
      title: 'IoT Sensor Data Processing Platform',
      description: 'Create a scalable IoT data processing platform for manufacturing',
      duration: '6 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Handle high-volume streaming sensor data',
        'Implement predictive maintenance analytics',
        'Build anomaly detection systems',
        'Create operational dashboards and alerts'
      ],
      content: 'Build an IoT data processing platform that handles millions of sensor readings, detects equipment anomalies, and enables predictive maintenance for manufacturing operations.'
    },
    {
      id: 'capstone-4',
      title: 'Portfolio Development and Interview Preparation',
      description: 'Create professional portfolio and prepare for senior ETL interviews',
      duration: '4 hours',
      difficulty: 'intermediate',
      order: 4,
      objectives: [
        'Create professional technical portfolio',
        'Develop compelling project presentations',
        'Master technical interview strategies',
        'Build professional GitHub presence'
      ],
      content: 'Transform your capstone projects into a professional portfolio. Learn to present technical work effectively and prepare for senior-level ETL developer and architect interviews.'
    }
  ]
};

export const CAREER_DEVELOPMENT_COURSE: ETLCourse = {
  id: 'etl-career-development',
  title: 'ETL Career Advancement & Professional Growth',
  description: 'Navigate your ETL career path from developer to architect. Master networking, skill development, and advancement strategies.',
  level: 'intermediate',
  totalDuration: '8-12 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Career',
  marketDemand: 'high',
  salaryRange: '$85,000 - $200,000',
  prerequisites: [
    'Basic ETL development experience',
    'Understanding of data concepts',
    'Professional communication skills',
    'Career development mindset'
  ],
  learningOutcomes: [
    'Create effective career advancement strategies',
    'Build professional network in data community',
    'Master salary negotiation techniques',
    'Develop leadership and mentoring skills',
    'Navigate job transitions and promotions',
    'Build personal brand as ETL professional'
  ],
  modules: [
    {
      id: 'career-1',
      title: 'ETL Career Paths and Advancement Strategies',
      description: 'Navigate the various career paths in ETL and data engineering',
      duration: '3 hours',
      difficulty: 'beginner',
      order: 1,
      objectives: [
        'Understand ETL career progression paths',
        'Identify required skills for each level',
        'Create personalized career development plans',
        'Master skill gap analysis techniques'
      ],
      content: 'Explore the various career paths in ETL and data engineering, from junior developer to chief data officer. Learn to create effective career advancement strategies and identify the skills needed for each level.'
    },
    {
      id: 'career-2',
      title: 'Professional Networking and Personal Branding',
      description: 'Build your professional network and establish your personal brand',
      duration: '3 hours',
      difficulty: 'intermediate',
      order: 2,
      objectives: [
        'Build effective professional networks',
        'Establish strong personal brand',
        'Master conference and community engagement',
        'Develop thought leadership presence'
      ],
      content: 'Learn to build meaningful professional relationships, establish your personal brand in the data community, and develop thought leadership through conferences, writing, and community engagement.'
    },
    {
      id: 'career-3',
      title: 'Interview Mastery and Salary Negotiation',
      description: 'Master technical interviews and salary negotiation strategies',
      duration: '3 hours',
      difficulty: 'intermediate',
      order: 3,
      objectives: [
        'Prepare for various interview formats',
        'Master technical problem-solving presentations',
        'Develop effective negotiation strategies',
        'Understand compensation structures'
      ],
      content: 'Master the art of technical interviews for ETL positions, from coding challenges to system design discussions. Learn effective salary negotiation techniques and understand modern compensation structures.'
    }
  ]
};

export const CERTIFICATION_PREP_COURSE: ETLCourse = {
  id: 'etl-certification-preparation',
  title: 'ETL Certification Preparation & Industry Standards',
  description: 'Prepare for major ETL and data engineering certifications. Master exam strategies and industry-standard practices.',
  level: 'intermediate',
  totalDuration: '12-16 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Certification',
  marketDemand: 'high',
  salaryRange: '$95,000 - $170,000',
  prerequisites: [
    'Solid ETL development foundation',
    'Experience with cloud platforms',
    'Understanding of data architecture',
    'Basic project experience'
  ],
  learningOutcomes: [
    'Pass major ETL and data engineering certifications',
    'Master exam-specific strategies and techniques',
    'Understand industry standards and best practices',
    'Build certification-worthy practical experience',
    'Develop continuing education strategies',
    'Leverage certifications for career advancement'
  ],
  modules: [
    {
      id: 'certification-1',
      title: 'AWS Data Engineer Certification Prep',
      description: 'Comprehensive preparation for AWS Certified Data Engineer certification',
      duration: '5 hours',
      difficulty: 'intermediate',
      order: 1,
      objectives: [
        'Master AWS data services and architectures',
        'Understand certification exam format and topics',
        'Practice with realistic exam scenarios',
        'Develop effective study strategies'
      ],
      content: 'Comprehensive preparation for the AWS Certified Data Engineer exam. Cover all exam domains including data ingestion, transformation, orchestration, and monitoring using AWS services.'
    },
    {
      id: 'certification-2',
      title: 'Microsoft Azure Data Engineer Certification',
      description: 'Prepare for Microsoft Azure Data Engineer Associate certification',
      duration: '4 hours',
      difficulty: 'intermediate',
      order: 2,
      objectives: [
        'Master Azure data platform services',
        'Understand exam objectives and structure',
        'Practice hands-on scenarios and labs',
        'Develop Azure-specific expertise'
      ],
      content: 'Prepare for the Microsoft Azure Data Engineer Associate certification. Focus on Azure data services, including Data Factory, Synapse Analytics, and Databricks integration patterns.'
    },
    {
      id: 'certification-3',
      title: 'Google Cloud and Vendor-Specific Certifications',
      description: 'Prepare for Google Cloud and major vendor certifications',
      duration: '3 hours',
      difficulty: 'intermediate',
      order: 3,
      objectives: [
        'Master Google Cloud data engineering services',
        'Understand vendor-specific certification paths',
        'Develop multi-cloud expertise',
        'Create comprehensive study plans'
      ],
      content: 'Prepare for Google Cloud Professional Data Engineer and major vendor certifications including dbt, Informatica, and Snowflake. Learn to navigate multiple certification paths effectively.'
    }
  ]
};